import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/lib/utils";
import { useOldSearch } from "@/src/app/Offers/EditOffer/useOldSearch";
import { Yacht } from "@/src/app/Yachts/model";
import { useBookingYachtMutation } from "@/src/lib/bookingYachtMutation";

/**
 * Pick a different yacht for an existing booking. Searches the fleet by name
 * through the same search API the offer builder uses; dates, locations,
 * pricing and extras stay on the booking and the broker reviews them after.
 */
export const ChangeYachtModal = ({
  open,
  setOpen,
  bookingId,
  currentYachtId,
  currentYachtName,
  brand,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  bookingId: string;
  currentYachtId: string;
  currentYachtName: string;
  brand?: "helm" | "anchor";
}) => {
  const { search, isSearching } = useOldSearch();
  const mutation = useBookingYachtMutation(bookingId);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Yacht[]>([]);
  const [selected, setSelected] = useState<Yacht | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelected(null);
    }
  }, [open]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      const brandParam = brand === "anchor" ? "Anchor" : "Helm";
      const res = await search(`?yacht_name=${encodeURIComponent(q)}&brand=${brandParam}&page=1&page_size=8`);
      setResults((res?.data ?? []).filter((y) => y.id !== currentYachtId));
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, brand, currentYachtId, search]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Change yacht</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            Currently <span className="font-medium">{currentYachtName}</span>. Search the fleet for the yacht this
            booking should be on. Dates, locations, pricing and extras stay as they are — review them after switching.
          </p>
          <Input
            autoFocus
            placeholder="Search yachts by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="flex max-h-[360px] flex-col gap-1 overflow-y-auto">
            {isSearching && <p className="px-2 py-3 text-sm text-gray-500">Searching…</p>}
            {!isSearching && query.trim().length >= 2 && results.length === 0 && (
              <p className="px-2 py-3 text-sm text-gray-500">No yachts match “{query.trim()}”.</p>
            )}
            {results.map((y) => (
              <button
                key={y.id}
                type="button"
                onClick={() => setSelected(y)}
                className={cn(
                  "flex items-center gap-3 rounded-lg border px-3 py-2 text-left hover:bg-slate-50",
                  selected?.id === y.id ? "border-blue-500 bg-blue-50" : "border-gray-200",
                )}
              >
                {y.media?.[0] ? (
                  <img src={y.media[0]} alt="" className="h-12 w-20 shrink-0 rounded object-cover" />
                ) : (
                  <div className="h-12 w-20 shrink-0 rounded bg-slate-100" />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{y.name}</span>
                  <span className="truncate text-xs text-gray-500">
                    {[y.modelName, y.year, y.maxPeople ? `${y.maxPeople} people` : null, y.cabins ? `${y.cabins} cabins` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!selected || mutation.isPending}
            onClick={() => {
              if (!selected) return;
              mutation.mutate(selected.id, { onSuccess: () => setOpen(false) });
            }}
          >
            {mutation.isPending ? "Changing…" : selected ? `Change to ${selected.name}` : "Change yacht"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
